import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const comparison = [
  { label: 'Qualidade do som', traditional: 'Ruidoso, reverberação, zonas mortas', silent: 'Cristalino direto no ouvido' },
  { label: 'Reclamações de barulho', traditional: 'Frequentes — risco de multa', silent: 'Zero — sem limite de horário' },
  { label: 'Estilos musicais', traditional: 'Um estilo por vez', silent: 'Vários DJs simultâneos' },
  { label: 'Controle de volume', traditional: 'Impossível — volume único', silent: 'Cada convidado ajusta o seu' },
  { label: 'Conforto para conversar', traditional: 'Precisa gritar para ser ouvido', silent: 'Tira o fone e conversa normalmente' },
  { label: 'Espaço necessário', traditional: 'Caixas, cabos, amplificadores', silent: 'Apenas uma mesa para os transmissores' },
  { label: 'Decoração visual', traditional: 'Caixas de som escondidas', silent: 'LEDs coloridos fazem parte da festa' },
  { label: 'Fotos e vídeos', traditional: 'Bom — iluminação comum', silent: 'Incrível — LEDs criam efeito único nas fotos' },
];

const benefits = [
  {
    icon: 'ri-moon-clear-line',
    title: 'Sem Limite de Horário',
    description: 'A lei do silêncio não se aplica. Sua festa pode continuar a noite inteira — sem uma única reclamação. Ideal para casamentos e eventos noturnos.',
    number: '24h',
    numberLabel: 'sem restrição de horário',
  },
  {
    icon: 'ri-group-line',
    title: 'Agrada Todos os Gostos',
    description: 'Até 10 canais = vários estilos musicais ao mesmo tempo. Os avós ouvem clássicos, os jovens ouvem eletrônico, todo mundo dança junto.',
    number: '3',
    numberLabel: 'DJs simultâneos',
  },
  {
    icon: 'ri-camera-line',
    title: 'Cenário Instagramável',
    description: 'Os LEDs coloridos dos fones criam um espetáculo visual na pista. Fotos e vídeos com efeito único que viralizam nas redes sociais.',
    number: '10x',
    numberLabel: 'mais engajamento nas redes',
  },
];

export default function BenefitsSection() {
  const { ref: compRef, isVisible: compVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: benRef, isVisible: benVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <>
      {/* Comparison table */}
      <section ref={compRef} className="py-28 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">
              Comparativo
            </span>
            <h2
              className={`font-heading font-bold text-5xl text-secondary transition-all duration-700 ${compVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              silent disco vs. festa tradicional
            </h2>
          </div>

          <div
            className={`bg-white rounded-3xl overflow-hidden border border-gray-100 transition-all duration-700 delay-200 ${compVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {/* Table header */}
            <div className="grid grid-cols-3 bg-secondary text-white">
              <div className="p-5 font-body text-sm font-semibold uppercase tracking-wider text-white/60">Critério</div>
              <div className="p-5 font-body text-sm font-semibold uppercase tracking-wider border-l border-white/10">Festa Tradicional</div>
              <div className="p-5 font-body text-sm font-semibold uppercase tracking-wider border-l border-white/10 text-primary">Festa Silenciosa</div>
            </div>

            {/* Rows */}
            {comparison.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-3 border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} transition-all duration-500 ${compVisible ? 'opacity-100' : 'opacity-0'}`}
                style={{ transitionDelay: `${200 + i * 60}ms` }}
              >
                <div className="p-5 font-body text-sm font-semibold text-secondary">{row.label}</div>
                <div className="p-5 border-l border-gray-100">
                  <span className="inline-flex items-center gap-2 font-body text-sm text-gray-500">
                    <i className="ri-close-circle-line text-red-400 text-base flex-shrink-0" />
                    {row.traditional}
                  </span>
                </div>
                <div className="p-5 border-l border-gray-100">
                  <span className="inline-flex items-center gap-2 font-body text-sm text-gray-700 font-medium">
                    <i className="ri-checkbox-circle-line text-primary text-base flex-shrink-0" />
                    {row.silent}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits highlight */}
      <section ref={benRef} className="py-28 bg-secondary">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2
              className={`font-heading font-bold text-5xl text-white mb-4 transition-all duration-700 ${benVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              por que sua festa merece isso
            </h2>
            <p className={`font-body text-lg text-white/60 transition-all duration-700 delay-150 ${benVisible ? 'opacity-100' : 'opacity-0'}`}>
              Resultados que transformam qualquer celebração em experiência memorável
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((b, i) => (
              <div
                key={i}
                className={`bg-white/5 border border-white/10 rounded-3xl p-10 text-center hover:bg-white/10 transition-all duration-500 ${benVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary/20 mx-auto mb-6">
                  <i className={`${b.icon} text-primary text-3xl`} />
                </div>
                <div className="font-heading font-extrabold text-5xl text-primary mb-1">{b.number}</div>
                <div className="font-body text-xs text-white/50 uppercase tracking-wider mb-5">{b.numberLabel}</div>
                <h3 className="font-heading font-bold text-xl text-white mb-3">{b.title}</h3>
                <p className="font-body text-sm text-white/65 leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}